import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button, cn, ToggleGroup, ToggleGroupItem } from "@adora/shadcn-ui";
import { addDataCy } from "../../utils/dom";
import { Z_PAGE_HEADER, Z_PAGE_TABS } from "../../constants/z-index";

export type WorkflowNavValue = "generate" | "approve" | "publish";

export interface WorkflowNavConfig {
  value: WorkflowNavValue;
  generateHref: string;
  approveHref: string;
  publishHref: string;
  /** Shown in the back button when on any of the workflow tabs. Pass project/theme name so it stays correct when switching tabs. */
  backLinkText: string;
  /** Back link URL. Defaults to "/projects" if omitted. */
  backLinkPath?: string;
}

export interface HeaderTabItem {
  value: string;
  label: string;
}

export interface HeaderTabsConfig {
  value: string;
  tabs: HeaderTabItem[];
  onValueChange: (value: string) => void;
}

export interface PageShadcnProps {
  title: string;
  description?: string;
  subDescription?: string;
  descriptionLoading?: boolean;
  backLinkText?: string;
  backLinkPath?: string;
  /** In place of `backLinkPath`, for a back link that does something else (e.g. go back in history). */
  onBack?: () => void;
  testId?: string;
  children?: ReactNode;
  stickyHeader?: ReactNode;
  titleBarActions?: ReactNode;
  showSubDescriptionOnHeader?: boolean;
  className?: string;
  /** Alignment of the main content area. Default "center". Use "left" for batch-group-style layouts. */
  contentAlign?: "left" | "center";
  /**
   * When set, the sticky header is always visible and the center shows a toggle group (Generate | Approve | Publish)
   * for switching between Generate Creatives, Approvals, and Publishing Ads within the same project. Current page is highlighted.
   */
  workflowNav?: WorkflowNavConfig;
  /**
   * Generic header tabs rendered in the same centered slot as workflowNav.
   * Use for in-page tab switching (e.g. Projects | Labels on settings pages).
   * Takes priority over the default title display but not over workflowNav.
   */
  headerTabs?: HeaderTabsConfig;
  /** Max width (px) for the description paragraph. Default 600. */
  descriptionMaxWidth?: number;
  /** Classes for the header block (title and description). */
  headerClassName?: string;
  /** Classes for the description paragraph. */
  descriptionClassName?: string;
}

export const PAGE_PADDING = 48;
export const TITLE_BAR_HEIGHT = 64;
export const STICKY_TITLE_HEIGHT = 0;
/** Offset for content that sticks below the title bar + sticky header (e.g. accordion triggers). Use in sticky top. */
export const STICKY_CONTENT_TOP = 128;

const PageShadcn = ({
  title,
  description,
  subDescription,
  descriptionLoading = false,
  backLinkText,
  backLinkPath,
  onBack,
  testId,
  children,
  stickyHeader,
  titleBarActions,
  showSubDescriptionOnHeader = true,
  className,
  contentAlign = "center",
  workflowNav,
  headerTabs,
  descriptionMaxWidth = 600,
  headerClassName,
  descriptionClassName
}: PageShadcnProps) => {
  const hasHeaderNav = !!(workflowNav || headerTabs);
  const [showTitleState, setShowTitleState] = useState(hasHeaderNav);
  const sentinelRef = useRef<globalThis.HTMLDivElement | null>(null);
  const pageStyles = {
    "--page-title-bar-height": `${TITLE_BAR_HEIGHT}px`,
    "--page-sticky-title-height": "0px",
    "--page-sticky-offset": `${TITLE_BAR_HEIGHT}px`,
    "--page-sticky-content-top": `${STICKY_CONTENT_TOP}px`
  } as CSSProperties;

  const stickyVisible = hasHeaderNav ? true : showTitleState;

  useEffect(() => {
    if (hasHeaderNav) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new globalThis.IntersectionObserver(
      ([entry]) => {
        setShowTitleState(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: `-${TITLE_BAR_HEIGHT}px 0px 0px 0px` }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasHeaderNav]);

  const resolvedBackPath = workflowNav ? (workflowNav.backLinkPath ?? "/projects") : backLinkPath;
  const resolvedBackText = workflowNav ? workflowNav.backLinkText : backLinkText;
  const backLinkButton = (
    <Button
      variant="ghost"
      className="w-full min-w-0 justify-start gap-1 px-2 text-orange-500 hover:bg-transparent hover:text-orange-600"
      // A path wraps this in a link, which carries the click and the action name.
      onClick={resolvedBackPath ? undefined : onBack}
      data-action-name={resolvedBackPath ? undefined : "page back link"}
    >
      <ChevronLeft className="h-4 w-4 shrink-0" />
      <span
        className="min-w-0 text-sm font-semibold truncate max-w-[120px] @3xl/app-content:max-w-[245px] text-left"
        title={resolvedBackText}
      >
        {resolvedBackText}
      </span>
    </Button>
  );

  return (
    <div className={cn("relative", className)} style={pageStyles}>
      <div
        className={cn(
          "sticky top-0 grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center bg-white px-2 transition-all duration-300 @3xl/app-content:px-4",
          stickyVisible && "border-b border-gray-200"
        )}
        style={{ height: TITLE_BAR_HEIGHT, zIndex: Z_PAGE_HEADER }}
      >
        {/* Every level down to the label needs `min-w-0`, or the label's `truncate` has nothing to
         * truncate against and the back link keeps its natural width — which is what let a long
         * project name run into the tabs once the agent drawer narrowed the content. */}
        <div className="min-w-0 overflow-hidden">
          {resolvedBackText && resolvedBackPath ? (
            <Link to={resolvedBackPath} data-action-name="page back link" className="block min-w-0">
              {backLinkButton}
            </Link>
          ) : (
            resolvedBackText && onBack && backLinkButton
          )}
        </div>
        <div className="min-w-0">
          {workflowNav ? (
            <ToggleGroup
              type="single"
              value={workflowNav.value}
              variant="default"
              size="sm"
              // Hidden below 768px: the phone header keeps only the back link and actions.
              className="hidden h-[64px] max-w-full gap-0 overflow-x-auto md:flex"
            >
              <ToggleGroupItem value="generate" asChild aria-label="Generate">
                <Link
                  to={workflowNav.generateHref}
                  state={{ projectName: workflowNav.backLinkText, fromProjects: true }}
                  className="h-[64px] border-b-2 border-transparent px-2 py-2 !text-sm text-gray-400 data-[state=on]:border-orange-500 data-[state=on]:bg-transparent data-[state=on]:text-gray-900 @3xl/app-content:px-4 @3xl/app-content:!text-[16px]"
                >
                  Generate
                </Link>
              </ToggleGroupItem>
              <ToggleGroupItem value="approve" asChild aria-label="Approve">
                <Link
                  to={workflowNav.approveHref}
                  state={{ projectName: workflowNav.backLinkText, fromProjects: true }}
                  className="h-[64px] border-b-2 border-transparent px-2 py-2 !text-sm text-gray-400 data-[state=on]:border-orange-500 data-[state=on]:bg-transparent data-[state=on]:text-gray-900 @3xl/app-content:px-4 @3xl/app-content:!text-[16px]"
                >
                  Approvals
                </Link>
              </ToggleGroupItem>
              <ToggleGroupItem value="publish" asChild aria-label="Publish">
                <Link
                  to={workflowNav.publishHref}
                  state={{ projectName: workflowNav.backLinkText, fromProjects: true }}
                  className="h-[64px] border-b-2 border-transparent px-2 py-2 !text-sm text-gray-400 data-[state=on]:border-orange-500 data-[state=on]:bg-transparent data-[state=on]:text-gray-900 @3xl/app-content:px-4 @3xl/app-content:!text-[16px]"
                >
                  Publishing
                </Link>
              </ToggleGroupItem>
            </ToggleGroup>
          ) : headerTabs ? (
            <ToggleGroup
              type="single"
              value={headerTabs.value}
              onValueChange={(val) => {
                if (val) headerTabs.onValueChange(val);
              }}
              variant="default"
              size="sm"
              className="gap-0 h-[64px]"
            >
              {headerTabs.tabs.map((tab) => (
                <ToggleGroupItem
                  key={tab.value}
                  value={tab.value}
                  aria-label={tab.label}
                  className="border-b-2 border-transparent px-4 py-2 !text-[16px] text-gray-400 data-[state=on]:border-orange-500 data-[state=on]:text-gray-900 data-[state=on]:bg-transparent h-[64px]"
                >
                  {tab.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          ) : (
            <div className="flex flex-col items-center">
              <h2
                className={cn(
                  "whitespace-nowrap text-center text-base text-gray-900 transition-all duration-500 ease-in-out",
                  stickyVisible ? "translate-y-0 opacity-100" : "-translate-y-8 opacity-0"
                )}
              >
                {title}
              </h2>
              {showSubDescriptionOnHeader && subDescription && (
                <p
                  className={cn(
                    "whitespace-nowrap text-[11px] text-gray-500 transition-all duration-500 ease-in-out",
                    stickyVisible ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
                  )}
                >
                  {subDescription}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex min-w-0 items-center justify-end" style={{ height: TITLE_BAR_HEIGHT }}>
          {titleBarActions}
        </div>
      </div>

      <div className="flex flex-col px-4 pt-8 @3xl/app-content:px-8 @5xl/app-content:px-12">
        <div className={cn("-mx-4 z-10 bg-white pb-6 @3xl/app-content:-mx-8 @5xl/app-content:-mx-12", headerClassName)}>
          <div className="flex justify-center pb-3" {...addDataCy(testId)}>
            <h1
              className={cn(
                "text-4xl font-instrument-serif text-gray-900 transition-all duration-500 ease-in-out @3xl/app-content:text-5xl",
                !hasHeaderNav && stickyVisible && "-translate-y-4 opacity-0",
                (hasHeaderNav || !stickyVisible) && "translate-y-0 opacity-100"
              )}
            >
              {title}
            </h1>
          </div>
          <div ref={sentinelRef} aria-hidden className="h-0 w-full" />
          <div className="flex justify-center">
            {descriptionLoading ? (
              <div className="h-5 w-[600px] animate-pulse rounded bg-gray-200" />
            ) : (
              <p
                className={cn(
                  "text-center text-sm text-gray-500 transition-all duration-500 ease-in-out",
                  !hasHeaderNav && stickyVisible && "-translate-y-2 opacity-0",
                  (hasHeaderNav || !stickyVisible) && "translate-y-0 opacity-100",
                  descriptionClassName
                )}
                style={{ maxWidth: `${descriptionMaxWidth}px` }}
              >
                {description}
              </p>
            )}
          </div>
          {showSubDescriptionOnHeader && subDescription && (
            <div className="flex justify-center">
              <p
                className={cn(
                  "max-w-[525px] text-center text-sm text-gray-500 transition-all duration-500 ease-in-out",
                  !hasHeaderNav && stickyVisible && "-translate-y-2 opacity-0",
                  (hasHeaderNav || !stickyVisible) && "translate-y-0 opacity-100"
                )}
              >
                {subDescription}
              </p>
            </div>
          )}
        </div>
      </div>

      {stickyHeader != null && (
        <div className="sticky bg-white" style={{ top: "var(--page-title-bar-height)", zIndex: Z_PAGE_TABS }}>
          {stickyHeader}
        </div>
      )}

      <div
        className={cn(
          "overflow-x-clip px-4 pb-8 @3xl/app-content:px-8 @5xl/app-content:px-12",
          contentAlign === "center" ? "text-center" : "text-left"
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default PageShadcn;
